import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Clear as ClearIcon } from '@mui/icons-material'
import { presentationAPI } from '../../services/api'
import { LUCIDE_ICONS, FALLBACK_ICON } from './lucideIcons'
import PathwayIcon from './PathwayIcon'

// What the UI assumes when the server list cannot be loaded: the icons
// bundled in this build and the validator's documented copy limits.
const BUNDLED = {
  icons: Object.keys(LUCIDE_ICONS),
  fallback: FALLBACK_ICON,
  limits: { display_name: 40, description: 120 },
}

let iconsPromise = null

export function loadPresentationIcons() {
  if (!iconsPromise) {
    iconsPromise = presentationAPI.getIcons().catch((err) => {
      iconsPromise = null
      throw err
    })
  }
  return iconsPromise
}

/**
 * Icon allowlist and copy limits from GET /api/presentation/icons.
 * Falls back to the bundled set (with `error` set) if the fetch fails.
 */
export function usePresentationIcons() {
  const [state, setState] = useState({ ...BUNDLED, loading: true, error: null })

  useEffect(() => {
    let cancelled = false
    loadPresentationIcons()
      .then((data) => {
        if (cancelled) return
        setState({
          icons: Array.isArray(data?.icons) && data.icons.length > 0 ? data.icons : BUNDLED.icons,
          fallback: data?.fallback || FALLBACK_ICON,
          limits: { ...BUNDLED.limits, ...(data?.limits || {}) },
          loading: false,
          error: null,
        })
      })
      .catch((err) => {
        console.error('Error loading presentation icons:', err)
        if (cancelled) return
        setState({
          ...BUNDLED,
          loading: false,
          error: 'Could not load the icon list from the server; showing the icons bundled in this build.',
        })
      })
    return () => {
      cancelled = true
    }
  }, [])

  return state
}

/**
 * Picks a lucide icon name from the server allowlist.
 *
 * `value` null means "inherit"; `placeholder` is the inherited icon shown
 * in that case (library value or type default).
 */
function IconPicker({ value, onChange, placeholder, disabled = false, error, helperText }) {
  const { icons, loading, error: loadError } = usePresentationIcons()
  const [anchorEl, setAnchorEl] = useState(null)

  const shown = value || placeholder
  // Keep a stored value that is no longer allowlisted visible so the admin
  // can see (and replace) it.
  const options = value && !icons.includes(value) ? [value, ...icons] : icons

  const choose = (name) => {
    onChange(name)
    setAnchorEl(null)
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Button
          variant="outlined"
          size="small"
          color={error ? 'error' : 'inherit'}
          disabled={disabled}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          startIcon={shown ? <PathwayIcon name={shown} size={16} /> : null}
          sx={{
            textTransform: 'none',
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
            color: value ? 'text.primary' : 'text.secondary',
            borderColor: error ? 'error.main' : 'divider',
          }}
        >
          {value || (placeholder ? `${placeholder} (default)` : 'Choose icon')}
        </Button>
        {value && !disabled && (
          <Tooltip title="Use the default icon">
            <IconButton size="small" onClick={() => onChange(null)}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {(error || helperText) && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          display="block"
          sx={{ mt: 0.5 }}
        >
          {error || helperText}
        </Typography>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, width: 320 }}>
          {loadError && (
            <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
              {loadError}
            </Alert>
          )}
          {loading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Box display="grid" gridTemplateColumns="repeat(6, 1fr)" gap={0.5}>
              {options.map((name) => {
                const selected = name === value
                const isDefault = !value && name === placeholder
                return (
                  <Tooltip key={name} title={name}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => choose(name)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 40,
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: selected ? 'primary.main' : isDefault ? 'text.disabled' : 'transparent',
                        borderStyle: isDefault && !selected ? 'dashed' : 'solid',
                        bgcolor: selected ? 'primary.50' : 'transparent',
                        color: selected ? 'primary.main' : 'text.primary',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <PathwayIcon name={name} size={20} />
                    </Box>
                  </Tooltip>
                )
              })}
            </Box>
          )}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography variant="caption" color="text.secondary">
              {placeholder ? `Default: ${placeholder}` : ''}
            </Typography>
            <Button size="small" onClick={() => choose(null)} disabled={!value}>
              Use default
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}

export default IconPicker
